import { useEffect, useRef, useState } from "react"
import { interceptExternalLinkClick } from "../lib/nativeShell"
import { AnimatePresence, motion } from "motion/react"

/**
 * The reCAPTCHA attribution, shown wherever a form calls `grecaptcha.execute`.
 *
 * It exists because the floating badge is hidden (`.grecaptcha-badge` in
 * input.css). Google's FAQ allows that — "You are allowed to hide the badge as
 * long as you include the reCAPTCHA branding visibly in the user flow" — and
 * this line is that branding, so the two have to travel together: hiding the
 * badge without it breaks the terms, and a form that runs a challenge without
 * rendering this has no branding at all.
 *
 * The branding itself stays on the page; only Google's two links fold into a
 * popover, since spelling both out puts three sentences of legal boilerplate
 * under a two-field form.
 */
const RecaptchaNotice = ({ className = "" }: { className?: string }) => {
    const [open, setOpen] = useState(false)
    const wrapRef = useRef<HTMLDivElement>(null)

    // Dismiss like the app's other popovers: a click anywhere outside, or Escape.
    useEffect(() => {
        if (!open) return
        const onClick = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false)
        }
        document.addEventListener("mousedown", onClick)
        document.addEventListener("keydown", onKey)
        return () => {
            document.removeEventListener("mousedown", onClick)
            document.removeEventListener("keydown", onKey)
        }
    }, [open])

    return (
        <div ref={wrapRef} className={`relative text-center ${className}`}>
            <p className="text-[11px] text-stone-400">
                Protected by reCAPTCHA.{" "}
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    className="underline underline-offset-2 hover:text-stone-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-action/60 rounded"
                >
                    Terms apply.
                </button>
            </p>

            <AnimatePresence>
                {open && (
                    <motion.div
                        // Downward, though the notice is the last thing in the
                        // card: upward would cover the submit button right above
                        // it. Overhanging the card's edge costs nothing.
                        initial={{ opacity: 0, scale: 0.95, y: -8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -8 }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        style={{ transformOrigin: "top center" }}
                        className="absolute top-full left-1/2 z-20 mt-2 w-44 -translate-x-1/2 rounded-xl bg-white p-1 text-left shadow-lg ring-1 ring-stone-200"
                    >
                        {[
                            { href: "https://policies.google.com/privacy", label: "Privacy Policy" },
                            { href: "https://policies.google.com/terms", label: "Terms of Service" },
                        ].map((item) => (
                            <a
                                key={item.href}
                                href={item.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={interceptExternalLinkClick}
                                className="block rounded-lg px-3 py-1.5 text-xs text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                            >
                                {item.label}
                            </a>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default RecaptchaNotice
